import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useUserRole } from "../../hooks/useUserRole.hook";
import { storageConst } from "../../utils/constants";
import { db } from "../../lib/firebase";
import MainButton from "../../components/Button";
import SpinnerIcon from "../../assets/svgs/spinner";

type FormData = {
  heading: string;
  description: string;
};

export default function PrivacyPolicy() {
  const [policy, setPolicy] = useState<FormData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const { register, handleSubmit, reset } = useForm<FormData>();
  const userRole = useUserRole(storageConst.APPTOKEN);
  const docId = process.env.REACT_APP_PRIVACY_POLICY!;

  useEffect(() => {
    const fetchPolicy = async () => {
      const policyDocRef = doc(db, "privacy", docId);
      const docSnap = await getDoc(policyDocRef);

      if (docSnap.exists()) {
        setPolicy(docSnap.data() as FormData);
        reset(docSnap.data() as FormData);
      } else {
        console.log("No such document!");
      }
      setIsFetching(false);
    };

    fetchPolicy();
  }, [reset]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);

    const policyDocRef = doc(db, "privacy", docId);

    try {
      await updateDoc(policyDocRef, {
        heading: data.heading,
        description: data.description,
      });

      alert("Policy updated successfully");
    } catch (error) {
      console.error("Error updating policy: ", error);
      alert("Error updating policy. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-bgCard rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-text mb-6 text-center">
        Privacy Policy
      </h1>

      {isFetching ? (
        // Skeleton Effect
        <div className="space-y-6 animate-pulse">
          <div className="space-y-4">
            <div className="h-4 bg-gray-300 rounded w-1/4"></div>
            <div className="h-12 bg-gray-300 rounded"></div>
          </div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-300 rounded w-1/4"></div>
            <div className="h-24 bg-gray-300 rounded"></div>
          </div>
          {userRole === "ADMIN" && (
            <div className="flex justify-end">
              <div className="h-12 bg-gray-300 rounded-full w-1/3 mx-auto"></div>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Heading Input */}
          <div>
            <label
              htmlFor="heading"
              className="block text-sm font-medium text-text"
            >
              Heading
            </label>
            <input
              id="heading"
              {...register("heading", { required: "Heading is required" })}
              disabled={userRole === "USER"}
              defaultValue={policy?.heading}
              className="w-full mt-1 p-3 border border-gray-300 rounded-full focus:ring-primary focus:border-primary bg-bg disabled:bg-bg"
              placeholder="Enter the heading"
            />
          </div>

          {/* Description Textarea */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-text"
            >
              Description
            </label>
            <textarea
              id="description"
              {...register("description", {
                required: "Description is required",
              })}
              disabled={userRole === "USER"}
              defaultValue={policy?.description}
              placeholder="Enter the description"
              className="w-full mt-1 p-3 min-h-[150px] border border-gray-300 focus:ring-primary focus:border-primary bg-bg disabled:bg-bg rounded-xl"
            ></textarea>
          </div>

          {/* Save Button */}
          {userRole === "ADMIN" && (
            <div className="flex justify-end">
              <MainButton
                type="submit"
                className="bg-primary hover:bg-primary/80 text-white py-3 px-6 rounded-full"
                label={isLoading ? <SpinnerIcon /> : "Save Changes"}
              />
            </div>
          )}
        </form>
      )}
    </div>
  );
}
